import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  HStack,
  PinInput,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawerFooter,
  CustomFormErrorMessage,
  CustomPinInputField,
  PrimaryButton,
  StatusBox,
} from '../../components/custom';
import {
  confirmTransactionPinValidation,
  transactionPinValidation,
} from '../../constants/validationConstants';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setPinAsync } from '../../store/slices/pin/setPinSlice';

interface CreateTransactionPinProps {
  setUserHasPin: React.Dispatch<React.SetStateAction<string | null>>;
}

export default function CreateTransactionPin({
  setUserHasPin,
}: CreateTransactionPinProps) {
  const appDispatch = useAppDispatch();

  const { status, value } = useAppSelector((state) => state.setPin);

  const initialValues = {
    pin: '',
    confirmPin: '',
  };

  const validationSchema = Yup.object().shape({
    pin: transactionPinValidation,
    confirmPin: confirmTransactionPinValidation,
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      appDispatch(
        setPinAsync({ pin: values.pin, confirmPin: values.confirmPin })
      );
    },
  });

  const {
    errors,
    touched,
    values,
    isValid,
    handleSubmit,
    setFieldTouched,
    setFieldValue,
  } = formik;

  if (status === 'loading') {
    return (
      <>
        <Box position="relative" zIndex="2" height="80vh" background="#fff">
          <AbsoluteCenter>
            <AnimatedLogo />
          </AbsoluteCenter>
        </Box>
      </>
    );
  }

  if (status === 'success') {
    return (
      <>
        <Box position="relative" zIndex="2" height="80vh" background="#fff">
          <AbsoluteCenter axis="vertical" top="45%" left="0" right="0">
            <StatusBox
              variant="success"
              title="Transaction PIN created successfully!"
              px={4}
            >
              {value?.message ?? 'Your pin has been set successfully'}
            </StatusBox>
          </AbsoluteCenter>
        </Box>

        <CustomDrawerFooter>
          <PrimaryButton w="full" onClick={() => setUserHasPin('true')}>
            Continue
          </PrimaryButton>
        </CustomDrawerFooter>
      </>
    );
  }

  return (
    <Box>
      <Box mt={4} mb={6}>
        <Text as="h1" color="#000" mb={1}>
          Create Transaction PIN
        </Text>
        <Text color="gray.300" fontSize=".875rem" lineHeight="171.5%">
          Make sure your pin is strong and unique.
        </Text>
      </Box>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={10}>
            <FormControl
              id="pin"
              isInvalid={Boolean(touched.pin && errors.pin)}
            >
              <FormLabel>PIN</FormLabel>
              <HStack>
                <PinInput
                  mask
                  value={values.pin}
                  onChange={(value) => {
                    setFieldValue('pin', value);
                  }}
                  isInvalid={Boolean(touched.pin && errors.pin)}
                >
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField
                    pinInputVariant="transactionPin"
                    onBlur={() => setFieldTouched('pin')}
                  />
                </PinInput>
              </HStack>
              <CustomFormErrorMessage>
                {touched.pin && errors.pin}
              </CustomFormErrorMessage>
            </FormControl>

            <FormControl
              id="confirmPin"
              isInvalid={Boolean(touched.confirmPin && errors.confirmPin)}
            >
              <FormLabel>Confirm PIN</FormLabel>
              <HStack>
                <PinInput
                  mask
                  value={values.confirmPin}
                  onChange={(value) => {
                    setFieldValue('confirmPin', value);
                  }}
                  isInvalid={Boolean(touched.confirmPin && errors.confirmPin)}
                >
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField pinInputVariant="transactionPin" />
                  <CustomPinInputField
                    pinInputVariant="transactionPin"
                    onBlur={() => setFieldTouched('confirmPin')}
                  />
                </PinInput>
              </HStack>
              <CustomFormErrorMessage>
                {touched.confirmPin && errors.confirmPin}
              </CustomFormErrorMessage>
            </FormControl>
          </Stack>

          <CustomDrawerFooter>
            <PrimaryButton type="submit" isDisabled={!isValid} w="full">
              Continue
            </PrimaryButton>
          </CustomDrawerFooter>
        </Form>
      </FormikProvider>
    </Box>
  );
}
